import { useState, useEffect } from 'react';
import {
  PhoneIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useAgency } from '../context/AgencyContext';
import {
  getAgencyUsage,
  getAgencyBusinessUsages,
  UsageAggregate,
} from '../services/api';

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

export default function UsageDashboardPage() {
  const { currentAgency, loading: agencyLoading } = useAgency();
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [usage, setUsage] = useState<UsageAggregate | null>(null);
  const [businessUsages, setBusinessUsages] = useState<UsageAggregate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentAgency) return;

    async function fetchUsage() {
      setLoading(true);
      setError(null);
      try {
        const [usageData, businessData] = await Promise.all([
          getAgencyUsage(currentAgency!.id, billingPeriod),
          getAgencyBusinessUsages(currentAgency!.id, billingPeriod),
        ]);
        setUsage(usageData);
        setBusinessUsages(businessData);
      } catch (err: any) {
        setError(err.message || 'Failed to load usage data');
      } finally {
        setLoading(false);
      }
    }

    fetchUsage();
  }, [currentAgency, billingPeriod]);

  if (agencyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Agency Selected</h3>
        <p className="mt-2 text-sm text-gray-500">
          Select an agency from the dropdown above.
        </p>
      </div>
    );
  }

  // Generate billing period options (last 12 months)
  const billingPeriodOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    billingPeriodOptions.push({ value, label });
  }

  // Calculate totals from Retell usage (voice calls)
  const totalCalls = usage?.totals.retellCallCount ?? 0;
  const totalMinutes = usage?.totals.retellMinutes ?? 0;
  const activeBusinesses = businessUsages.filter(bu =>
    (bu.totals.retellMinutes ?? 0) > 0
  ).length;

  const stats = [
    {
      name: 'Total Calls',
      value: totalCalls.toLocaleString(),
      icon: PhoneIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Total Duration',
      value: formatDuration((usage?.totals.retellMinutes ?? 0) * 60),
      icon: ClockIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Active Clients',
      value: activeBusinesses.toString(),
      icon: UserGroupIcon,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Overview</h1>
          <p className="mt-1 text-sm text-gray-500">
            AI receptionist activity for {currentAgency.name}
          </p>
        </div>
        <div>
          <select
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6"
          >
            {billingPeriodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Info Banner */}
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <ChartBarIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              This dashboard shows your AI receptionist activity. Your subscription includes all calls and features.
              Usage is tracked for informational purposes only.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
            {stats.map((stat) => (
              <div
                key={stat.name}
                className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6"
              >
                <dt>
                  <div className={`absolute rounded-md ${stat.color} p-3`}>
                    <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <p className="ml-16 truncate text-sm font-medium text-gray-500">
                    {stat.name}
                  </p>
                </dt>
                <dd className="ml-16 flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </dd>
              </div>
            ))}
          </div>

          {/* Per-Business Breakdown */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Usage by Client
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Call activity breakdown per business
              </p>
            </div>
            <div className="overflow-x-auto">
              {businessUsages.length === 0 ? (
                <div className="text-center py-12">
                  <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    No usage data
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Usage will appear here once calls are made.
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Business
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                      >
                        Calls
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                      >
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {businessUsages.map((bu) => {
                      const callMinutes = bu.totals.retellMinutes ?? 0;
                      const callCount = bu.totals.retellCallCount ?? 0;
                      return (
                        <tr key={bu.businessId}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {bu.businessName || bu.businessId}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                            {callCount.toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                            {formatDuration(callMinutes * 60)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6">
                        Total
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-semibold text-gray-900">
                        {totalCalls.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-semibold text-gray-900">
                        {formatDuration(totalMinutes * 60)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
